/**
 * Duplication d'une fiche bien (C-Level).
 *
 * `buildDuplicateDraft` part de la fiche source et produit un brouillon
 * pré-rempli pour le formulaire de création :
 *  - copie TOUTES les colonnes "annonce" (descriptions, points forts, mailing,
 *    structurel, PEB live, etc.) ;
 *  - VIDE l'identifiant unique (`referenceInterne`) — le C-Level en saisit un
 *    nouveau avant d'enregistrer ;
 *  - REMET À ZÉRO ce qui est propre à la fiche source : état de publication,
 *    cycle ELS/PEB, infos locataire/contrat, décompte SRS ;
 *  - `photos: []` — les fichiers sont copiés physiquement après création
 *    (cf. copyBienPhotos), pas via le tableau source (qui pointe sur le
 *    storage du bien d'origine).
 *
 * Rien n'est écrit en base ici : le brouillon vit côté formulaire jusqu'au
 * clic "Créer".
 */

import { supabase } from "@/lib/supabase";
import type { BienRow } from "@/schemas/bien";
import type { BienPhoto } from "@/types/bien";

const BUCKET = "biens-photos";

/** Colonnes techniques / non copiables (PK + horodatage gérés par la DB). */
const DROP_FIELDS = ["id", "reference", "created_at", "updated_at"] as const;

/** Cycle ELS / PEB — remis à zéro (le duplicata redémarre le process). */
const CYCLE_RESET_EMPTY = [
  // `ancienPrestataireEls` retiré : ce n'est pas un prestataire mais l'ancien
  // champ date « Transfert élec fait le », remplacé par `transfertElecDate`
  // (migration 048). Sa suppression en base est écrite dans la migration 107.
  "prestataireEle", "prestataireEls",
  "prestataireEleRefusePar", "edlDatePrevue", "edlHeure", "edlCommentaire",
  // `statutEls` retiré : colonne supprimée par la migration 112.
  "statutEle", "expertPeb", "pebDatePrevue", "pebHeure",
  "pebCommentaire", "pebCertificatPdf", "statutPeb",
  "nouvClassePeb", "nouvCertificatPeb", "nouvPebFinLe", "nouvConsoKwh",
  "nouvEmissionCo2", "nouvConsoTheorique", "nouvChauffageEnergie",
  "nouvPebCertificatPdf", "expertPebRefusePar",
] as const;
const CYCLE_RESET_NULL = [
  "prestataireEleRefuseAt", "expertPebRefuseAt", "transfertElecDate",
  // Un duplicata n'hérite jamais d'un cycle ouvert (migration 135).
  "cycleOuvertLe",
] as const;
const CYCLE_RESET_FLAGS = [
  "eleNonFait", "transfertElecFait", "appartPretPhotos",
  "dossierCloture", "dossierClos",
] as const;

/** Infos locataire / contrat — propres au bail en cours, vidées. */
const TENANT_RESET = ["renonRecuLe", "finDeContrat"] as const;

/**
 * Décompte SRS — montants de sortie du locataire précédent, vidés.
 *
 * Les six montants du décompte détaillé n'y figurent plus depuis le
 * 2026-08-10 : ils ont été retirés de la fiche, le décompte se résume au
 * total. Ce tableau ne doit lister que des colonnes que le front assume — la
 * migration 107 les supprime, et un `UPDATE` qui nomme une colonne absente
 * fait échouer la duplication entière.
 */
const SRS_RESET = [
  "indemnitePreavis", "garantieLiberee",
  "totalSrs", "commentaireSrs",
] as const;

/** Construit le brouillon pré-rempli pour dupliquer `source`. */
export function buildDuplicateDraft(source: BienRow): Record<string, unknown> {
  const draft: Record<string, unknown> = { ...(source as Record<string, unknown>) };

  for (const k of DROP_FIELDS) delete draft[k];
  for (const k of CYCLE_RESET_EMPTY) draft[k] = "";
  for (const k of CYCLE_RESET_NULL) draft[k] = null;
  for (const k of CYCLE_RESET_FLAGS) draft[k] = null;
  for (const k of TENANT_RESET) draft[k] = "";
  for (const k of SRS_RESET) draft[k] = "";

  // Identité : on vide l'identifiant unique (referenceInterne).
  draft.referenceInterne = "";

  // Publication : le duplicata repart en brouillon, jamais publié.
  draft.statutPublication = "Brouillon";
  draft.last_published_at = null;
  draft.content_updated_at = null;

  // Photos copiées physiquement après création (cf. copyBienPhotos).
  draft.photos = [];

  // Brouillon actif → surlignage rouge/orange des champs manquants.
  draft.disponibilite = "EN PRÉPARATION";

  return draft;
}

/** Champs propres à la LOCATION — vidés quand on duplique vers une fiche VENTE
 *  (fix backlog « fix vente prix/loyer » : un duplicata vente gardait les
 *  loyers/charges de la fiche location source). Strings → "" (JAMAIS null,
 *  les FormField crashent sur null.length), arrays → []. */
const LOCATION_ONLY_EMPTY = [
  "loyerMensuel", "chargesMensuelles", "parkingLoyerMensuel",
  "caveLoyerMensuel", "garageLoyerMensuel", "renonRecuLe", "finDeContrat",
] as const;
const LOCATION_ONLY_ARRAYS = ["chargesIncluses"] as const;

/** Champs propres à la VENTE — vidés quand on duplique vers une fiche LOCATION. */
const VENTE_ONLY_EMPTY = ["prix", "prixM2"] as const;

/**
 * Vide dans `draft` les champs qui n'ont pas de sens pour la transaction
 * cible. À appliquer sur le résultat de `buildDuplicateDraft` dans les DEUX
 * formulaires de création (location : BienCreateFormPage ; vente :
 * BienVenteCreateFormPage) au moment d'hydrater le brouillon dupliqué.
 */
export function stripTransactionSpecific(
  draft: Record<string, unknown>,
  target: "location" | "vente",
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...draft };
  if (target === "vente") {
    for (const k of LOCATION_ONLY_EMPTY) out[k] = "";
    for (const k of LOCATION_ONLY_ARRAYS) out[k] = [];
  } else {
    for (const k of VENTE_ONLY_EMPTY) out[k] = "";
  }
  return out;
}

/**
 * Copie physiquement les photos de `sourceId` vers `targetId` dans le storage,
 * et retourne le nouveau tableau `photos` (chemins sous le nouvel id).
 * Best-effort : en cas d'échec sur un fichier, on le saute (le duplicata reste
 * créé, l'utilisateur peut re-uploader).
 */
export async function copyBienPhotos(
  sourcePhotos: BienPhoto[] | null | undefined,
  targetId: string,
): Promise<BienPhoto[]> {
  if (!sourcePhotos || sourcePhotos.length === 0) return [];
  const out: BienPhoto[] = [];
  for (const p of sourcePhotos) {
    try {
      const ext = p.path.split(".").pop() || "jpg";
      const newId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `p-${Math.random().toString(36).slice(2, 10)}`;
      const newPath = `${targetId}/${newId}.${ext}`;
      const { error } = await supabase.storage.from(BUCKET).copy(p.path, newPath);
      if (error) continue;
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(newPath);
      out.push({ id: newId, path: newPath, url: data.publicUrl });
    } catch {
      // best-effort : on saute ce fichier
    }
  }
  return out;
}
